import { useSelector, useDispatch } from 'react-redux';
import { useMemo, useCallback } from 'react';
import Api from '../Network';
import { setUserInfo } from '../redux/action';
import {
  clearPermissionCache,
  flattenUserPermissions,
  getPermissionTitle,
} from '../Utilis/permissionHelpers';

export interface Permission {
  id: string;
  title: string;
  module: string;
  action: string;
}

export interface Role {
  id: number;
  name: string;
  permissions: Permission[];
}

export interface UserPermissions {
  roles: Role[];
  permissions: Permission[];
  userType: number;
  isAdmin: boolean;
}

export const usePermissions = () => {
  const { userInfo } = useSelector((state: any) => state.setReducer);
  const dispatch = useDispatch();

  const userPermissions = useMemo((): UserPermissions => {
    if (!userInfo) {
      return {
        roles: [],
        permissions: [],
        userType: 0,
        isAdmin: false,
      };
    }

    const roles = userInfo.roles || [];
    const allPermissions = flattenUserPermissions(userInfo);

    return {
      roles,
      permissions: allPermissions,
      userType: userInfo.type ?? -1,
      isAdmin: !!(userInfo.isAdmin || userInfo.type === 0),
    };
  }, [userInfo]);

  /**
   * Refresh user permissions from the backend.
   * Clears the local permission cache so the sidebar/route guards re-sync.
   */
  const refreshPermissions = useCallback(async () => {
    try {
      clearPermissionCache();
      const userResponse = await Api.post('auth/me', {});
      dispatch(setUserInfo(userResponse));
      return userResponse;
    } catch (error) {
      console.error('Failed to refresh user permissions:', error);
      throw error;
    }
  }, [dispatch]);

  const hasPermission = useCallback(
    (permissionTitle: string): boolean => {
      if (userPermissions.isAdmin) return true;
      if (!permissionTitle) return false;

      return userPermissions.permissions.some(
        (permission) => getPermissionTitle(permission) === permissionTitle
      );
    },
    [userPermissions.isAdmin, userPermissions.permissions]
  );

  const hasAnyPermission = useCallback(
    (permissionTitles: string[]): boolean => {
      if (userPermissions.isAdmin) return true;

      const validTitles = permissionTitles.filter(
        (title): title is string => typeof title === 'string' && title.length > 0
      );
      if (validTitles.length === 0) return false;

      return validTitles.some((title) => hasPermission(title));
    },
    [userPermissions.isAdmin, hasPermission]
  );

  const hasAllPermissions = useCallback(
    (permissionTitles: string[]): boolean => {
      if (userPermissions.isAdmin) return true;

      const validTitles = permissionTitles.filter(
        (title): title is string => typeof title === 'string' && title.length > 0
      );
      if (validTitles.length === 0) return false;

      return validTitles.every((title) => hasPermission(title));
    },
    [userPermissions.isAdmin, hasPermission]
  );

  const can = useCallback(
    (module: string, action: string): boolean => {
      const permissionTitleWithManage = `manage_${module}_${action}`;
      const permissionTitleWithoutManage = `${module}_${action}`;

      return (
        hasPermission(permissionTitleWithManage) ||
        hasPermission(permissionTitleWithoutManage)
      );
    },
    [hasPermission]
  );

  const canView = useCallback(
    (module: string): boolean => can(module, 'view'),
    [can]
  );
  const canCreate = useCallback(
    (module: string): boolean => can(module, 'create'),
    [can]
  );
  const canEdit = useCallback(
    (module: string): boolean => can(module, 'edit'),
    [can]
  );
  const canDelete = useCallback(
    (module: string): boolean => can(module, 'delete'),
    [can]
  );
  const canShow = useCallback(
    (module: string): boolean => can(module, 'show'),
    [can]
  );

  const hasRole = useCallback(
    (roleIds: number[]): boolean => {
      if (userPermissions.isAdmin) return true;

      return userPermissions.roles.some((role) => roleIds.includes(role.id));
    },
    [userPermissions.isAdmin, userPermissions.roles]
  );

  const getModulePermissions = useCallback(
    (module: string): Permission[] => {
      return userPermissions.permissions.filter(
        (permission) => permission.module === module
      );
    },
    [userPermissions.permissions]
  );

  const canAccessRoute = useCallback(
    (routePermissions: string[]): boolean => {
      if (userPermissions.isAdmin) return true;

      return hasAnyPermission(routePermissions);
    },
    [userPermissions.isAdmin, hasAnyPermission]
  );

  return {
    userPermissions,
    isAdmin: userPermissions.isAdmin,
    userType: userPermissions.userType,

    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    can,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canShow,

    hasRole,

    getModulePermissions,
    canAccessRoute,

    refreshPermissions,
  };
};

export default usePermissions;
